import { useEffect, useState, useCallback } from 'react';
import { ref, get, set } from 'firebase/database';
import { database } from '../firebase/firebase';
import { UserProfile, AnonymousUser, BankInfo, User } from '../types';
import { generateId } from '../utils/helpers';
import { useAuth } from './useAuth';
import { getEventSelection as getStoredEventSelection, saveEventSelection as saveStoredEventSelection } from '../utils/eventSelections';

// Pure resolver, kept at module scope so its identity is stable across renders
// (as a hook-local closure it silently broke every effect that depended on it).
export const getDisplayName = (
    userProfile: UserProfile | null,
    firebaseUser: User | null
): string => {
    if (userProfile?.displayName) return userProfile.displayName;
    if (firebaseUser?.displayName) return firebaseUser.displayName;
    if (firebaseUser?.email) return firebaseUser.email;
    return 'User';
};

export const useUser = () => {
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    const { user, loading: authLoading } = useAuth();

    // Get or create user profile
    const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
        try {
            const userRef = ref(database, `users/${uid}`);
            const snapshot = await get(userRef);
            return snapshot.exists() ? snapshot.val() : null;
        } catch (error) {
            console.error('Error getting user profile:', error);
            return null;
        }
    };

    // Create user profile
    const createUserProfile = async (user: User): Promise<UserProfile> => {
        const profile: UserProfile = {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            createdAt: Date.now(),
            bankInfo: null,
            eventSelections: {}
        };

        try {
            const userRef = ref(database, `users/${user.uid}`);
            await set(userRef, profile);

            const userListRef = ref(database, `userList/${user.uid}`);
            await set(userListRef, true);

            return profile;
        } catch (error) {
            console.error('Error creating user profile:', error);
            throw error;
        }
    };

    const updateDisplayName = async (userId: string, displayName: string) => {
        try {
            const userRef = ref(database, `users/${userId}/displayName`);
            await set(userRef, displayName);

            setUserProfile(prev => prev ? { ...prev, displayName } : null);
        } catch (error) {
            console.error('Error updating display name:', error);
            throw error;
        }
    };

    const updateBankInfo = async (uid: string, bankInfo: BankInfo): Promise<void> => {
        try {
            const bankInfoRef = ref(database, `users/${uid}/bankInfo`);
            await set(bankInfoRef, bankInfo);

            if (userProfile && userProfile.uid === uid) {
                setUserProfile({ ...userProfile, bankInfo });
            }
        } catch (error) {
            console.error('Error updating bank info:', error);
            throw error;
        }
    };

    // **NEW: Function to copy user's bank info to event user**
    const copyUserBankInfoToEvent = async (eventId: string, userId: string): Promise<void> => {
        try {
            // Get user's bank info
            const userRef = ref(database, `users/${userId}`);
            const userSnapshot = await get(userRef);

            if (userSnapshot.exists()) {
                const userProfile: UserProfile = userSnapshot.val();
                if (userProfile.bankInfo) {
                    // Copy to eventUsers
                    const eventUserBankRef = ref(database, `eventUsers/${eventId}/${userId}/bankInfo`);
                    await set(eventUserBankRef, userProfile.bankInfo);
                }
            }
        } catch (error) {
            console.error('Error copying bank info to event:', error);
            // Don't throw - this is optional
        }
    };

    // **NEW: Create or update event user with bank info copy**
    const getOrCreateEventUser = async (eventId: string, userId: string, displayName: string, isAnonymous: boolean = false): Promise<void> => {
        try {
            const eventUserRef = ref(database, `eventUsers/${eventId}/${userId}`);
            const snapshot = await get(eventUserRef);

            if (!snapshot.exists()) {
                // Create new event user
                const eventUser = {
                    userId,
                    displayName,
                    isAnonymous,
                    createdAt: Date.now(),
                    lastAccess: Date.now()
                };

                await set(eventUserRef, eventUser);

                // **Copy bank info from user profile if logged in user**
                if (!isAnonymous) {
                    await copyUserBankInfoToEvent(eventId, userId);
                }
            } else {
                // Update lastAccess
                await set(ref(database, `eventUsers/${eventId}/${userId}/lastAccess`), Date.now());
            }
        } catch (error) {
            console.error('Error creating/updating event user:', error);
            throw error;
        }
    };

    const saveEventSelection = async (uid: string, eventId: string, selectedName: string): Promise<void> => {
        try {
            const selectionRef = ref(database, `users/${uid}/eventSelections/${eventId}`);
            await set(selectionRef, selectedName);

            if (userProfile && userProfile.uid === uid) {
                setUserProfile({
                    ...userProfile,
                    eventSelections: {
                        ...userProfile.eventSelections,
                        [eventId]: selectedName
                    }
                });
            }
        } catch (error) {
            console.error('Error saving event selection:', error);
            throw error;
        }
    };

    const getEventSelection = (uid: string, eventId: string): string | null => {
        if (userProfile && userProfile.uid === uid) {
            return userProfile.eventSelections?.[eventId] || null;
        }
        return null;
    };

    const getOrCreateAnonymousUser = async (eventId: string): Promise<AnonymousUser> => {
        const storedSelection = getStoredEventSelection(eventId);

        if (storedSelection && storedSelection.startsWith('~u_')) {
            try {
                const anonRef = ref(database, `eventUsers/${eventId}/${storedSelection}`);
                const snapshot = await get(anonRef);

                if (snapshot.exists()) {
                    const anonUser = snapshot.val();
                    await set(ref(database, `eventUsers/${eventId}/${storedSelection}/lastAccess`), Date.now());
                    return anonUser;
                }
            } catch (error) {
                console.error('Error getting anonymous user:', error);
            }
        }

        const anonymousId = `~u_${generateId()}`;
        const newAnonymousUser: AnonymousUser = {
            anonymousId,
            displayName: `Guest ${anonymousId.slice(-4)}`,
            createdAt: Date.now(),
            lastAccess: Date.now()
        };

        try {
            const anonRef = ref(database, `eventUsers/${eventId}/${anonymousId}`);
            await set(anonRef, newAnonymousUser);

            saveStoredEventSelection(eventId, anonymousId);

            return newAnonymousUser;
        } catch (error) {
            console.error('Error creating anonymous user:', error);
            throw error;
        }
    };

    // ✅ Sử dụng useCallback để tránh tạo function mới mỗi render
    const initializeUser = useCallback(async (user: User | null): Promise<void> => {
        setLoading(true);

        if (user) {
            let profile = await getUserProfile(user.uid);

            if (!profile) {
                profile = await createUserProfile(user);
            } else {
                if (!profile.displayName && user.displayName) {
                    await updateDisplayName(user.uid, user.displayName);
                    profile.displayName = user.displayName;
                }
            }

            setUserProfile(profile);
        } else {
            setUserProfile(null);
        }

        setLoading(false);
    }, []); // ✅ Empty dependency array vì function này không phụ thuộc vào state

    // Firebase restores the session asynchronously, so the very first render always
    // has a null user. The old guard ran exactly once — right then — and latched, so
    // the profile stayed empty for the rest of the session and every screen fell back
    // to the Google account name instead of the one set in the profile.
    useEffect(() => {
        if (authLoading) return;
        initializeUser(user);
    }, [user, authLoading, initializeUser]);

    return {
        userProfile,
        loading,
        initializeUser,
        updateDisplayName,
        updateBankInfo,
        saveEventSelection,
        getEventSelection,
        getOrCreateAnonymousUser,
        getOrCreateEventUser,
        copyUserBankInfoToEvent,
        getDisplayName
    };
};